import ApiError from 'api/ApiError';
import { PaymentPurpose } from './paymentPurpose';
import { PaymentPromptSourcePage } from './paymentPromptAnalytics';

export const OFFER_DETAILS_PAYMENT_REQUIRED_CODE = 'OFFER_DETAILS_PAYMENT_REQUIRED';
export const OFFER_DETAILS_PAYMENT_UNAVAILABLE_CODE = 'OFFER_DETAILS_PAYMENT_UNAVAILABLE';

export type OfferDetailsPaymentBlock = {
  code: typeof OFFER_DETAILS_PAYMENT_REQUIRED_CODE | typeof OFFER_DETAILS_PAYMENT_UNAVAILABLE_CODE;
  locationId: number | null;
  jobId: number | null;
  offerId: number | null;
  paymentPolicy: 'standard_revenue' | '123dentist_corporate' | null;
  paymentPurpose: PaymentPurpose | null;
  corporateManaged: boolean;
  requiresCardSetup: boolean;
  isCardInvalid: boolean;
  reason: string;
};

const toPositiveId = (value: unknown): number | null => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

export const getOfferDetailsPaymentBlock = (error: unknown): OfferDetailsPaymentBlock | null => {
  const apiError = error as ApiError;
  const data = apiError?.data;
  if (apiError?.status !== 402 && apiError?.status !== 503) return null;
  if (data?.code !== OFFER_DETAILS_PAYMENT_REQUIRED_CODE
    && data?.code !== OFFER_DETAILS_PAYMENT_UNAVAILABLE_CODE) return null;

  return {
    code: data.code,
    locationId: toPositiveId(data.locationId),
    jobId: toPositiveId(data.jobId),
    offerId: toPositiveId(data.offerId),
    paymentPolicy: data.paymentPolicy || null,
    paymentPurpose: data.paymentPurpose || null,
    corporateManaged: Boolean(data.corporateManaged),
    requiresCardSetup: Boolean(data.requiresCardSetup),
    isCardInvalid: Boolean(data.isCardInvalid),
    reason: data.reason || 'payment_context_unavailable',
  };
};

export const buildOfferDetailsPaymentInfoRoute = ({
  offerId,
  jobId,
  locationId,
  paymentPurpose,
  sourcePage,
}: {
  offerId: number;
  jobId?: number | string;
  locationId: number;
  paymentPurpose: PaymentPurpose;
  sourcePage: PaymentPromptSourcePage;
}) => ({
  pathname: 'paymentInfo',
  state: {
    locationId,
    nextPage: 'customOfferDetails',
    offerId,
    jobId,
    paymentPurpose,
    sourcePage,
    checkpointOrigin: 'offer_details_view' as const,
  },
});

export const shouldPrefetchOfferResume = (
  offer: { offerDetailsPaymentProtected?: boolean } | null | undefined,
): boolean => !offer?.offerDetailsPaymentProtected;

/** Fetch protected details before recording the authoritative office view. */
export const loadProtectedOfferDetails = async <T>({
  offerId,
  getOffer,
  updateViews,
}: {
  offerId: number;
  getOffer: (input: { offerId: number }) => Promise<T>;
  updateViews: (input: { offerId: number }) => Promise<unknown>;
}): Promise<T> => {
  const offer = await getOffer({ offerId });
  await updateViews({ offerId });
  return offer;
};
